import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Slider,
  Stack,
  TextField,
  Typography,
  MenuItem,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Celebration as CelebrationIcon,
  ContentCopy as ContentCopyIcon,
  Favorite as FavoriteIcon,
  Groups as GroupsIcon,
  History as HistoryIcon,
  Savings as SavingsIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTransactions } from '../contexts/TransactionsContext'
import { loadGroups, upsertGroup } from '../utils/groupsStorage'

const ORANGE = '#FF8A00'
const SURFACE = 'rgba(16,11,38,0.92)'
const SOFT = 'rgba(255,255,255,0.03)'
const BORDER = '1px solid rgba(255,255,255,0.08)'

const countries = [
  { code: 'SL', name: 'Sierra Leone', currency: 'SLE' },
  { code: 'LR', name: 'Liberia', currency: 'LRD' },
  { code: 'GN', name: 'Guinea', currency: 'GNF' },
  { code: 'GM', name: 'Gambia', currency: 'GMD' },
]

const fieldSx = {
  '& .MuiInputBase-root': { bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 4, color: '#fff' },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.62)' },
  '& .MuiInputLabel-root.Mui-focused': { color: ORANGE },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,138,0,0.38)' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE },
}

const money = (amount, currency) => `${currency} ${Number(amount || 0).toFixed(2)}`
const makeId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`
const makeInviteCode = () => `OSU${Date.now().toString().slice(-7)}`
const makeFundInviteCode = () => `FND${Date.now().toString().slice(-7)}`

const HubCard = ({ icon, title, subtitle, onClick }) => (
  <Card onClick={onClick} sx={{ borderRadius: 5, background: 'linear-gradient(135deg, rgba(28,18,68,0.98) 0%, rgba(63,39,148,0.94) 100%)', border: BORDER, cursor: 'pointer', '&:hover': { borderColor: 'rgba(255,138,0,0.28)' } }}>
    <CardContent sx={{ p: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
          <Box sx={{ width: 54, height: 54, borderRadius: 3, bgcolor: 'rgba(91,46,255,0.18)', display: 'grid', placeItems: 'center', color: ORANGE }}>{icon}</Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20 }}>{title}</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.8, lineHeight: 1.55 }}>{subtitle}</Typography>
          </Box>
        </Stack>
        <Typography sx={{ color: 'rgba(255,255,255,0.54)', fontSize: 28, fontWeight: 300 }}>›</Typography>
      </Stack>
    </CardContent>
  </Card>
)

const Pick = ({ title, subtitle, onClick, trailing, selected = false }) => (
  <Card onClick={onClick} sx={{ borderRadius: 5, bgcolor: SURFACE, border: selected ? `1px solid ${ORANGE}` : BORDER, cursor: 'pointer', '&:hover': { borderColor: 'rgba(255,138,0,0.28)' } }}>
    <CardContent sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20 }}>{title}</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.8, lineHeight: 1.55 }}>{subtitle}</Typography>
        </Box>
        {trailing ? <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontWeight: 800 }}>{trailing}</Typography> : null}
      </Stack>
    </CardContent>
  </Card>
)

const LabelRow = ({ label, value, accent = false }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
    <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontWeight: 700 }}>{label}</Typography>
    <Typography sx={{ color: accent ? ORANGE : '#fff', fontWeight: accent ? 900 : 800, textAlign: 'right' }}>{value}</Typography>
  </Box>
)

export default function Contributions() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { transactions, addTransaction } = useTransactions()
  const [view, setView] = useState('hub')
  const [joinInput, setJoinInput] = useState('')
  const [joinError, setJoinError] = useState('')
  const [receipt, setReceipt] = useState(null)
  const [selectedGroupId, setSelectedGroupId] = useState(null)
  const [fundJoinInput, setFundJoinInput] = useState('')
  const [fundJoinError, setFundJoinError] = useState('')
  const [fundReceipt, setFundReceipt] = useState(null)
  const [selectedFundId, setSelectedFundId] = useState(null)
  const [copied, setCopied] = useState('')
  const [allGroups, setAllGroups] = useState(() => loadGroups().filter((group) => group.kind === 'osusu'))
  const [allFunds, setAllFunds] = useState(() => loadGroups().filter((group) => group.kind === 'community_fund'))
  const [refreshKey, setRefreshKey] = useState(0)
  const [form, setForm] = useState({
    groupName: 'United we stand',
    countryCode: 'SL',
    contributionAmount: '10',
    frequency: 'Monthly',
    payoutOrder: 'Random',
    members: 3,
    startDate: new Date().toISOString().slice(0, 10),
  })
  const [fundForm, setFundForm] = useState({
    cause: 'Wedding',
    countryCode: 'SL',
    goalAmount: '1000',
    description: '',
    privacy: 'PRIVATE',
  })
  const currentActorKey = user?.email || user?.id || ''
  const currentActorName = user?.name || 'MojoPayr User'

  const selectedCountry = useMemo(() => countries.find((c) => c.code === form.countryCode) || countries[0], [form.countryCode])
  const selectedGroup = useMemo(() => allGroups.find((group) => group.id === selectedGroupId) || null, [allGroups, selectedGroupId])
  const selectedFundCountry = useMemo(() => countries.find((c) => c.code === fundForm.countryCode) || countries[0], [fundForm.countryCode])
  const selectedFund = useMemo(() => allFunds.find((fund) => fund.id === selectedFundId) || null, [allFunds, selectedFundId])
  const osusuHistory = useMemo(() => transactions.filter((tx) => tx.type === 'osusu_contribution' || tx.type === 'osusu_circle_created'), [transactions])
  const myGroups = useMemo(() => allGroups.filter((group) => currentActorKey && (group.createdBy === currentActorKey || (group.membersJoined || []).some((member) => member.email === currentActorKey))), [allGroups, currentActorKey])
  const myFunds = useMemo(() => allFunds.filter((fund) => currentActorKey && (fund.createdBy === currentActorKey || (fund.contributors || []).some((member) => member.email === currentActorKey))), [allFunds, currentActorKey])

  useEffect(() => {
    setAllGroups(loadGroups().filter((group) => group.kind === 'osusu'))
    setAllFunds(loadGroups().filter((group) => group.kind === 'community_fund'))
  }, [refreshKey])

  const summary = useMemo(() => {
    const amount = Number(form.contributionAmount || 0)
    const members = Number(form.members || 0)
    return {
      amount,
      members,
      pot: amount * members,
      rotation: form.payoutOrder === 'Random' ? 'Randomized once group fills' : 'Join order payout',
    }
  }, [form])

  const fundSummary = useMemo(() => {
    const goal = Number(fundForm.goalAmount || 0)
    return {
      goal,
      privacy: fundForm.privacy,
      cause: fundForm.cause || 'Community Fund',
    }
  }, [fundForm])

  const groupStats = useMemo(() => {
    if (!selectedGroup) return null
    const joined = selectedGroup.membersJoined || []
    const payments = selectedGroup.payments || []
    const pooled = payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0)
    const currentUser = joined.find((member) => member.email === currentActorKey)
    return {
      joinedCount: joined.length,
      targetMembers: Number(selectedGroup.members || 0),
      pooled,
      currentCycle: 1,
      nextPayout: joined.length ? joined[0].name : 'Pending',
      currentUser,
      nextDueDate: selectedGroup.startDate,
    }
  }, [selectedGroup, currentActorKey])

  const fundStats = useMemo(() => {
    if (!selectedFund) return null
    const contributors = selectedFund.contributors || []
    const escrow = contributors.reduce((sum, item) => sum + Number(item.amount || 0), 0)
    const currentUser = contributors.find((item) => item.email === currentActorKey)
    const goal = Number(selectedFund.goalAmount || 0)
    return {
      escrow,
      goal,
      percent: goal > 0 ? Math.min(100, (escrow / goal) * 100) : 0,
      currentUser,
    }
  }, [selectedFund, currentActorKey])

  const updateForm = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }))
  const updateFundForm = (key) => (event) => setFundForm((current) => ({ ...current, [key]: event.target.value }))

  const copyText = async (value, type) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(type)
      setTimeout(() => setCopied(''), 1800)
    } catch {
      setCopied('')
    }
  }

  const createOsusu = () => {
    if (!form.groupName.trim() || !summary.amount || !summary.members) return
    const id = makeId('osusu')
    const inviteCode = makeInviteCode()
    const inviteLink = `${window.location.origin}/contribute/${id}?invite=${inviteCode}`
    const group = {
      id,
      kind: 'osusu',
      name: form.groupName.trim(),
      countryCode: form.countryCode,
      countryName: selectedCountry.name,
      currency: selectedCountry.currency,
      contributionAmount: summary.amount,
      frequency: form.frequency,
      payoutOrder: form.payoutOrder,
      members: summary.members,
      startDate: form.startDate,
      nextDueDate: form.startDate,
      inviteCode,
      inviteLink,
      createdBy: currentActorKey,
      organizerName: currentActorName,
      membersJoined: [],
      payments: [],
      status: 'draft',
      createdAt: new Date().toISOString(),
    }
    upsertGroup(group)
    setRefreshKey((current) => current + 1)
    addTransaction({
      type: 'osusu_circle_created',
      status: 'completed',
      currency: group.currency,
      amount: group.contributionAmount,
      title: `${group.name} created`,
      counterpart: group.name,
      meta: { groupId: group.id, inviteCode: group.inviteCode, receiptType: 'osusu_circle_created' },
    })
    setSelectedGroupId(group.id)
    setView('detail')
  }

  const openGroupByInvite = () => {
    const raw = joinInput.trim()
    if (!raw) return setJoinError('Enter invite code or link first.')
    const inviteCode = raw.includes('invite=') ? decodeURIComponent(raw.split('invite=').pop() || '') : raw
    const linkId = raw.includes('/contribute/') ? raw.split('/contribute/').pop()?.split('?')[0] : ''
    const found = allGroups.find((group) => group.inviteCode === inviteCode || group.id === linkId)
    if (!found) return setJoinError('Invite code or link not found.')
    setJoinError('')
    setSelectedGroupId(found.id)
    setView('detail')
  }

  const contributeToCycle = () => {
    if (!selectedGroup || !groupStats) return
    const currentEmail = currentActorKey
    const joined = [...(selectedGroup.membersJoined || [])]
    let member = joined.find((item) => item.email === currentEmail)
    if (!member) {
      member = { email: currentEmail, name: currentActorName, position: joined.length + 1 }
      joined.push(member)
    }
    const payment = { id: makeId('osusu_pay'), amount: Number(selectedGroup.contributionAmount || 0), currency: selectedGroup.currency, memberName: member.name, memberEmail: member.email, cycle: 1, createdAt: new Date().toISOString() }
    const nextGroup = { ...selectedGroup, status: 'active', membersJoined: joined, payments: [payment, ...(selectedGroup.payments || [])] }
    upsertGroup(nextGroup)
    setRefreshKey((current) => current + 1)
    const saved = addTransaction({
      type: 'osusu_contribution',
      status: 'completed',
      currency: selectedGroup.currency,
      amount: payment.amount,
      title: `${member.name} contributed to ${selectedGroup.name}`,
      counterpart: selectedGroup.name,
      meta: { groupId: selectedGroup.id, inviteCode: selectedGroup.inviteCode, cycle: 1, receiptType: 'osusu_contribution' },
    })
    setReceipt({
      id: saved.id,
      circle: selectedGroup.name,
      amount: payment.amount,
      currency: selectedGroup.currency,
      frequency: selectedGroup.frequency.toUpperCase(),
      cycle: 1,
      member: member.email.replace('@', '_'),
      inviteCode: selectedGroup.inviteCode,
      dateText: new Date(saved.createdAt).toLocaleString(),
    })
    setSelectedGroupId(selectedGroup.id)
    setView('receipt')
  }

  const createFund = () => {
    if (!fundSummary.cause.trim()) return
    const id = makeId('fund')
    const inviteCode = makeFundInviteCode()
    const inviteLink = `${window.location.origin}/contributions?fund=${id}&invite=${inviteCode}`
    const fund = {
      id,
      kind: 'community_fund',
      name: fundSummary.cause.trim(),
      countryCode: fundForm.countryCode,
      countryName: selectedFundCountry.name,
      currency: selectedFundCountry.currency,
      goalAmount: fundSummary.goal,
      description: fundForm.description.trim(),
      privacy: fundForm.privacy,
      inviteCode,
      inviteLink,
      createdBy: currentActorKey,
      organizerName: currentActorName,
      contributors: [],
      status: 'active',
      createdAt: new Date().toISOString(),
    }
    upsertGroup(fund)
    setRefreshKey((current) => current + 1)
    addTransaction({
      type: 'community_fund_created',
      status: 'completed',
      currency: fund.currency,
      amount: fund.goalAmount,
      title: `${fund.name} fund created`,
      counterpart: fund.name,
      meta: { fundId: fund.id, inviteCode: fund.inviteCode, receiptType: 'community_fund_created' },
    })
    setSelectedFundId(fund.id)
    setView('community-detail')
  }

  const openFundByInvite = () => {
    const raw = fundJoinInput.trim()
    if (!raw) return setFundJoinError('Enter fund invite code or link first.')
    const inviteCode = raw.includes('invite=') ? decodeURIComponent(raw.split('invite=').pop() || '') : raw
    const linkId = raw.includes('fund=') ? raw.split('fund=').pop()?.split('&')[0] : raw.includes('/contribute/') ? raw.split('/contribute/').pop()?.split('?')[0] : ''
    const found = allFunds.find((fund) => fund.inviteCode === inviteCode || fund.id === linkId)
    if (!found) return setFundJoinError('Fund invite code or link not found.')
    setFundJoinError('')
    setSelectedFundId(found.id)
    setView('community-detail')
  }

  const contributeToFund = (amount = 100) => {
    if (!selectedFund || !fundStats) return
    const currentEmail = currentActorKey
    const contributors = [...(selectedFund.contributors || [])]
    const contribution = {
      id: makeId('fund_pay'),
      amount: Number(amount || 0),
      currency: selectedFund.currency,
      memberName: currentActorName,
      email: currentEmail,
      createdAt: new Date().toISOString(),
    }
    contributors.unshift(contribution)
    const nextFund = { ...selectedFund, contributors }
    upsertGroup(nextFund)
    setRefreshKey((current) => current + 1)
    const saved = addTransaction({
      type: 'community_fund_contribution',
      status: 'completed',
      currency: selectedFund.currency,
      amount: contribution.amount,
      title: `${contribution.memberName} supported ${selectedFund.name}`,
      counterpart: selectedFund.name,
      meta: { fundId: selectedFund.id, inviteCode: selectedFund.inviteCode, receiptType: 'community_fund_contribution' },
    })
    setFundReceipt({
      id: saved.id,
      fund: selectedFund.name,
      amount: contribution.amount,
      currency: selectedFund.currency,
      member: currentEmail.replace('@', '_'),
      inviteCode: selectedFund.inviteCode,
      goalAmount: selectedFund.goalAmount,
      dateText: new Date(saved.createdAt).toLocaleString(),
    })
    setSelectedFundId(selectedFund.id)
    setView('community-receipt')
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#09061B', py: { xs: 4, md: 6 } }}>
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Button startIcon={<ArrowBackIcon />} onClick={() => (view === 'hub' ? navigate('/dashboard') : setView('hub'))} sx={{ color: '#fff', textTransform: 'none', fontWeight: 700 }}>
              {view === 'hub' ? 'Back' : 'Back to Hub'}
            </Button>
          </Stack>

          {view === 'hub' && (
            <>
              <Card sx={{ borderRadius: 6, overflow: 'hidden', background: 'linear-gradient(135deg, rgba(34,18,82,0.98) 0%, rgba(91,46,255,0.94) 62%, rgba(255,138,0,0.12) 100%)', border: BORDER }}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Typography sx={{ color: '#fff', fontSize: { xs: 30, md: 42 }, fontWeight: 950 }}>Save together with structure and trust</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.78)', mt: 1.2, maxWidth: 760, lineHeight: 1.6 }}>
                    Use Osusu for rotating payouts and community funds for targeted goals like school fees, weddings, funerals, and emergencies.
                  </Typography>
                </CardContent>
              </Card>

              <Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.76)', fontWeight: 700, mb: 2 }}>Rotating Osusu</Typography>
                <Stack spacing={2}>
                  <HubCard icon={<SavingsIcon />} title="Create Rotating Osusu" subtitle="Start a trusted savings circle with clear payout order." onClick={() => setView('create')} />
                  <HubCard icon={<GroupsIcon />} title="Join Osusu" subtitle="Enter an invite code and make your first contribution." onClick={() => setView('join')} />
                  <HubCard icon={<GroupsIcon />} title="My Osusu Groups" subtitle="Track active circles, payout progress, and completed rounds." onClick={() => setView('my-groups')} />
                  <HubCard icon={<HistoryIcon />} title="Osusu History" subtitle="Review contributions, payouts, cycles, and receipt references." onClick={() => setView('history')} />
                </Stack>
              </Box>

              <Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.76)', fontWeight: 700, mb: 2 }}>Community Fund</Typography>
                <Stack spacing={2}>
                  <HubCard icon={<CelebrationIcon />} title="Create Community Fund" subtitle="Wedding, birthday, funeral, school fees, or custom goal." onClick={() => setView('community-create')} />
                  <HubCard icon={<GroupsIcon />} title="Join Community Fund" subtitle="Support a private fundraiser with an invite code." onClick={() => setView('community-join')} />
                  <HubCard icon={<FavoriteIcon />} title="My Community Funds" subtitle="Review the funds you created, joined, or contributed to." onClick={() => setView('community-list')} />
                </Stack>
              </Box>
            </>
          )}

          {view === 'create' && (
            <>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 34 }}>Create Rotating Osusu</Typography>
              <Card sx={{ borderRadius: 5, background: 'linear-gradient(135deg, rgba(34,18,82,0.98) 0%, rgba(91,46,255,0.94) 100%)', border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 18 }}>Build a trusted rotating savings circle</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 1, lineHeight: 1.6 }}>Set the amount, choose the schedule, and make the payout order clear before inviting everyone in.</Typography>
                </CardContent>
              </Card>

              <Stack spacing={2.2}>
                <TextField fullWidth label="Group Name" value={form.groupName} onChange={updateForm('groupName')} sx={fieldSx} />
                <TextField select fullWidth label="Country" value={form.countryCode} onChange={updateForm('countryCode')} sx={fieldSx}>
                  {countries.map((country) => <MenuItem key={country.code} value={country.code}>{country.name}</MenuItem>)}
                </TextField>
                <TextField fullWidth label="Contribution Amount" value={form.contributionAmount} onChange={updateForm('contributionAmount')} sx={fieldSx} />
                <TextField select fullWidth label="Frequency" value={form.frequency} onChange={updateForm('frequency')} sx={fieldSx}>
                  {['Weekly', 'Monthly', 'Quarterly'].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                </TextField>

                <Box>
                  <Typography sx={{ color: '#fff', fontWeight: 800, mb: 1.2 }}>Payout Order</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))' }, gap: 2 }}>
                    {[
                      { id: 'Random', title: 'Random', subtitle: 'Fair shuffle order' },
                      { id: 'Join Order', title: 'Join Order', subtitle: 'First in, first paid' },
                    ].map((option) => (
                      <Card key={option.id} onClick={() => setForm((current) => ({ ...current, payoutOrder: option.id }))} sx={{ borderRadius: 4, bgcolor: form.payoutOrder === option.id ? 'rgba(255,138,0,0.18)' : SURFACE, border: form.payoutOrder === option.id ? `1px solid ${ORANGE}` : BORDER, cursor: 'pointer' }}>
                        <CardContent sx={{ p: 2.5 }}>
                          <Typography sx={{ color: form.payoutOrder === option.id ? ORANGE : '#fff', fontWeight: 900 }}>{option.title}</Typography>
                          <Typography sx={{ color: 'rgba(255,255,255,0.68)', mt: 0.7 }}>{option.subtitle}</Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>

                <Box>
                  <Typography sx={{ color: '#fff', fontWeight: 800, mb: 1.1 }}>Number of Members: {summary.members}</Typography>
                  <Slider min={3} max={20} step={1} value={summary.members} onChange={(_, value) => setForm((current) => ({ ...current, members: value }))} sx={{ color: ORANGE }} />
                </Box>

                <TextField fullWidth type="date" label="Start Date" value={form.startDate} onChange={updateForm('startDate')} sx={fieldSx} InputLabelProps={{ shrink: true }} />

                <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 900, mb: 2 }}>Circle Summary</Typography>
                    <Stack spacing={1.2}>
                      <LabelRow label="Each member pays" value={money(summary.amount, selectedCountry.currency)} />
                      <LabelRow label="Members" value={`${summary.members} people`} />
                      <LabelRow label="Full payout pot" value={summary.amount ? money(summary.pot, selectedCountry.currency) : '-'} />
                      <LabelRow label="Rotation" value={summary.rotation} />
                    </Stack>
                  </CardContent>
                </Card>

                <Button variant="contained" onClick={createOsusu} sx={{ borderRadius: 999, py: 1.4, textTransform: 'none', fontWeight: 900, bgcolor: ORANGE }}>
                  Create Osusu
                </Button>
              </Stack>
            </>
          )}

          {view === 'join' && (
            <>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 34 }}>Join Osusu</Typography>
              <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2.2}>
                    <TextField fullWidth label="Invite Link or Invite Code" value={joinInput} onChange={(e) => setJoinInput(e.target.value)} sx={fieldSx} placeholder="Paste invite link or enter invite code" />
                    {joinError ? <Typography sx={{ color: '#ff8a8a', fontWeight: 700 }}>{joinError}</Typography> : null}
                    <Button variant="contained" onClick={openGroupByInvite} sx={{ borderRadius: 999, py: 1.4, textTransform: 'none', fontWeight: 900, bgcolor: ORANGE }}>
                      Open Group
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </>
          )}

          {view === 'detail' && selectedGroup && groupStats && (
            <>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 34 }}>Osusu Group</Typography>
              <Card sx={{ borderRadius: 5, background: 'linear-gradient(135deg, rgba(34,18,82,0.98) 0%, rgba(91,46,255,0.94) 100%)', border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20 }}>{selectedGroup.name}</Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.78)', mt: 1.2 }}>Contribution: {money(selectedGroup.contributionAmount, selectedGroup.currency)}</Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.78)', mt: 0.7 }}>Frequency: {selectedGroup.frequency}</Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.78)', mt: 0.7 }}>Start Date: {selectedGroup.startDate}</Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.78)', mt: 0.7 }}>Next Due Date: {groupStats.nextDueDate}</Typography>
                    </Box>
                    <Chip label={selectedGroup.status?.toUpperCase() || 'DRAFT'} sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 900 }} />
                  </Stack>
                  <Box sx={{ mt: 2.4, height: 8, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.16)', overflow: 'hidden' }}>
                    <Box sx={{ width: `${groupStats.targetMembers ? (groupStats.joinedCount / groupStats.targetMembers) * 100 : 0}%`, height: '100%', bgcolor: ORANGE }} />
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.78)', mt: 1.1 }}>{groupStats.joinedCount}/{groupStats.targetMembers} members joined</Typography>
                </CardContent>
              </Card>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))' }, gap: 2 }}>
                <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}><CardContent sx={{ p: 3 }}><Typography sx={{ color: 'rgba(255,255,255,0.62)' }}>Current Cycle</Typography><Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 30, mt: 1 }}>{groupStats.currentCycle}</Typography><Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.7 }}>Rotation in progress</Typography></CardContent></Card>
                <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}><CardContent sx={{ p: 3 }}><Typography sx={{ color: 'rgba(255,255,255,0.62)' }}>Pooled Amount</Typography><Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 30, mt: 1 }}>{money(groupStats.pooled, selectedGroup.currency)}</Typography><Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.7 }}>Waiting for payout</Typography></CardContent></Card>
                <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}><CardContent sx={{ p: 3 }}><Typography sx={{ color: 'rgba(255,255,255,0.62)' }}>Invite Code</Typography><Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 26, mt: 1 }}>{selectedGroup.inviteCode}</Typography><Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.7 }}>Share with trusted members</Typography></CardContent></Card>
                <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}><CardContent sx={{ p: 3 }}><Typography sx={{ color: 'rgba(255,255,255,0.62)' }}>Next Payout</Typography><Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 26, mt: 1 }}>{groupStats.nextPayout}</Typography><Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.7 }}>{groupStats.joinedCount ? 'Position 1' : 'Circle completed'}</Typography></CardContent></Card>
              </Box>

              <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20, mb: 2 }}>Your Circle Status</Typography>
                  <Stack spacing={1.2}>
                    <LabelRow label="Role" value={selectedGroup.createdBy === currentActorKey ? 'Organizer' : 'Member'} />
                    <LabelRow label="Your payout position" value={groupStats.currentUser ? String(groupStats.currentUser.position) : '-'} />
                    <LabelRow label="Current cycle status" value={groupStats.currentUser ? 'Contribution pending' : 'Join to participate'} />
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20, mb: 1.2 }}>Invite Link</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.7, wordBreak: 'break-all' }}>{selectedGroup.inviteLink}</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
                    <Button startIcon={<ContentCopyIcon />} variant="outlined" onClick={() => copyText(selectedGroup.inviteCode, 'code')} sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800, color: '#fff', borderColor: 'rgba(255,255,255,0.16)' }}>
                      {copied === 'code' ? 'Invite Code Copied' : 'Copy Invite Code'}
                    </Button>
                    <Button startIcon={<ContentCopyIcon />} variant="outlined" onClick={() => copyText(selectedGroup.inviteLink, 'link')} sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800, color: '#fff', borderColor: 'rgba(255,255,255,0.16)' }}>
                      {copied === 'link' ? 'Invite Link Copied' : 'Copy Invite Link'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20, mb: 1.2 }}>How this circle works</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.7 }}>
                    Members contribute on schedule, and the payout order is {selectedGroup.payoutOrder === 'Random' ? 'randomized once the circle is full.' : 'based on join order.'}
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20, mb: 1.8 }}>Members & Payout Order</Typography>
                  {groupStats.joinedCount === 0 ? (
                    <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>No members yet. Share the invite code to fill the circle and start contributions.</Typography>
                  ) : (
                    <Stack spacing={1.3}>
                      {selectedGroup.membersJoined.map((member) => (
                        <Box key={member.email} sx={{ p: 2, borderRadius: 4, bgcolor: SOFT, border: BORDER }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography sx={{ color: '#fff', fontWeight: 900 }}>{member.name}</Typography>
                              <Typography sx={{ color: ORANGE, mt: 0.4 }}>Awaiting contribution</Typography>
                            </Box>
                            <Typography sx={{ color: 'rgba(255,255,255,0.78)' }}>Position {member.position}</Typography>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>

              <Button variant="contained" onClick={contributeToCycle} sx={{ borderRadius: 999, py: 1.4, textTransform: 'none', fontWeight: 900, bgcolor: ORANGE }}>
                {groupStats.currentUser ? 'Contribute for Next Cycle' : 'Join This Circle'}
              </Button>
            </>
          )}

          {view === 'receipt' && receipt && (
            <>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 34 }}>Osusu Receipt</Typography>
              <Card sx={{ borderRadius: 6, background: 'linear-gradient(135deg, rgba(34,18,82,0.98) 0%, rgba(91,46,255,0.94) 100%)', border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 28 }}>Contribution Recorded</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.74)', mt: 1.2, lineHeight: 1.6 }}>Your Osusu contribution has been logged with a digital proof trail.</Typography>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={1.25}>
                    <LabelRow label="Circle" value={receipt.circle} />
                    <LabelRow label="Receipt Ref" value={receipt.id} />
                    <LabelRow label="Status" value="SUCCESS" />
                    <LabelRow label="Date" value={receipt.dateText} />
                    <LabelRow label="Action" value="Cycle 1 contribution" />
                    <LabelRow label="Member" value={receipt.member} />
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={1.25}>
                    <LabelRow label="Amount" value={money(receipt.amount, receipt.currency)} />
                    <LabelRow label="Circle Frequency" value={receipt.frequency} />
                    <LabelRow label="Current Cycle" value={String(receipt.cycle)} />
                    <LabelRow label="Invite Code" value={receipt.inviteCode} />
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.74)', lineHeight: 1.7 }}>This receipt is stored in MojoPayr activity so circle members can track contributions and payouts with more trust.</Typography>
                </CardContent>
              </Card>

              <Button variant="contained" onClick={() => setView('detail')} sx={{ borderRadius: 999, py: 1.4, textTransform: 'none', fontWeight: 900, bgcolor: ORANGE }}>
                Back to Group
              </Button>
            </>
          )}

          {view === 'my-groups' && (
            <>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 34 }}>My Osusu Groups</Typography>
              {myGroups.length === 0 ? (
                <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}><CardContent sx={{ p: 3 }}><Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>No Osusu groups yet. Create one or join with an invite code.</Typography></CardContent></Card>
              ) : (
                <Stack spacing={2}>
                  {myGroups.map((group) => <Pick key={group.id} title={group.name} subtitle={`${group.frequency} • ${money(group.contributionAmount, group.currency)} • ${group.countryName}`} trailing={group.status?.toUpperCase() || 'DRAFT'} onClick={() => { setSelectedGroupId(group.id); setView('detail') }} />)}
                </Stack>
              )}
            </>
          )}

          {view === 'history' && (
            <>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 34 }}>Osusu History</Typography>
              {osusuHistory.length === 0 ? (
                <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}><CardContent sx={{ p: 3 }}><Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>No Osusu receipt references yet.</Typography></CardContent></Card>
              ) : (
                <Stack spacing={2}>
                  {osusuHistory.map((tx) => (
                    <Card key={tx.id} sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                      <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" justifyContent="space-between" spacing={2}>
                          <Box>
                            <Typography sx={{ color: '#fff', fontWeight: 900 }}>{tx.title || tx.counterpart || 'Osusu activity'}</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.68)', mt: 0.7 }}>{new Date(tx.createdAt).toLocaleString()}</Typography>
                            <Typography sx={{ color: ORANGE, mt: 0.7 }}>{tx.type === 'osusu_contribution' ? 'Contribution receipt' : 'Circle created'}</Typography>
                          </Box>
                          <Typography sx={{ color: '#fff', fontWeight: 900 }}>{money(tx.amount, tx.currency || 'SLE')}</Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </>
          )}

          {view === 'community-create' && (
            <>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 34 }}>Create Community Fund</Typography>
              <Stack spacing={2.2}>
                <TextField select fullWidth label="Cause" value={fundForm.cause} onChange={updateFundForm('cause')} sx={fieldSx}>
                  {['Wedding', 'Birthday', 'Funeral', 'School Fees', 'Emergency', 'Custom Goal'].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                </TextField>
                <TextField select fullWidth label="Country" value={fundForm.countryCode} onChange={updateFundForm('countryCode')} sx={fieldSx}>
                  {countries.map((country) => <MenuItem key={country.code} value={country.code}>{country.name}</MenuItem>)}
                </TextField>
                <TextField fullWidth label="Goal Amount (Optional)" value={fundForm.goalAmount} onChange={updateFundForm('goalAmount')} sx={fieldSx} />
                <TextField fullWidth label="Description" value={fundForm.description} onChange={updateFundForm('description')} sx={fieldSx} multiline minRows={3} />
                <TextField select fullWidth label="Visibility" value={fundForm.privacy} onChange={updateFundForm('privacy')} sx={fieldSx}>
                  {['PRIVATE', 'PUBLIC'].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                </TextField>
                <Button variant="contained" onClick={createFund} sx={{ borderRadius: 999, py: 1.4, textTransform: 'none', fontWeight: 900, bgcolor: ORANGE }}>
                  Create Fund
                </Button>
              </Stack>
            </>
          )}

          {view === 'community-join' && (
            <>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 34 }}>Join Community Fund</Typography>
              <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2.2}>
                    <TextField fullWidth label="Invite Code" value={fundJoinInput} onChange={(e) => setFundJoinInput(e.target.value)} sx={fieldSx} placeholder="Enter fund invite code or paste link" />
                    {fundJoinError ? <Typography sx={{ color: '#ff8a8a', fontWeight: 700 }}>{fundJoinError}</Typography> : null}
                    <Button variant="contained" onClick={openFundByInvite} sx={{ borderRadius: 999, py: 1.4, textTransform: 'none', fontWeight: 900, bgcolor: ORANGE }}>
                      Join Fund
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </>
          )}

          {view === 'community-list' && (
            <>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 34 }}>My Community Funds</Typography>
              {myFunds.length === 0 ? (
                <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}><CardContent sx={{ p: 3 }}><Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>No community funds yet. Create one or join with an invite code.</Typography></CardContent></Card>
              ) : (
                <Stack spacing={2}>
                  {myFunds.map((fund) => {
                    const raised = (fund.contributors || []).reduce((sum, item) => sum + Number(item.amount || 0), 0)
                    return <Pick key={fund.id} title={fund.name} subtitle={`Goal: ${fund.goalAmount ? money(fund.goalAmount, fund.currency) : 'Open'} • Raised: ${money(raised, fund.currency)}`} trailing={fund.status?.toUpperCase() || 'ACTIVE'} onClick={() => { setSelectedFundId(fund.id); setView('community-detail') }} />
                  })}
                </Stack>
              )}
            </>
          )}

          {view === 'community-detail' && selectedFund && fundStats && (
            <>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 34 }}>Fund Details</Typography>
              <Card sx={{ borderRadius: 5, background: 'linear-gradient(135deg, rgba(34,18,82,0.98) 0%, rgba(91,46,255,0.94) 100%)', border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 26 }}>{selectedFund.name}</Typography>
                  <Chip label={selectedFund.status?.toUpperCase() || 'ACTIVE'} sx={{ mt: 1.2, bgcolor: 'rgba(44,200,110,0.16)', color: '#8FF1AF', fontWeight: 900 }} />
                  <Typography sx={{ color: ORANGE, mt: 2, fontWeight: 900 }}>Escrow: {money(fundStats.escrow, selectedFund.currency)}</Typography>
                  <Box sx={{ mt: 1.2, height: 8, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.16)', overflow: 'hidden' }}>
                    <Box sx={{ width: `${fundStats.percent}%`, height: '100%', bgcolor: ORANGE }} />
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.9 }}>{fundStats.percent.toFixed(0)}% funded</Typography>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2.2}>
                    <TextField fullWidth label="Contribution Amount" defaultValue="100" sx={fieldSx} id="fund-contribution-amount" />
                    <Button variant="contained" onClick={() => {
                      const raw = typeof document !== 'undefined' ? document.getElementById('fund-contribution-amount')?.value : '100'
                      contributeToFund(Number(raw || 0))
                    }} sx={{ borderRadius: 999, py: 1.4, textTransform: 'none', fontWeight: 900, bgcolor: ORANGE }}>
                      Contribute
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20, mb: 1.2 }}>Invite Access</Typography>
                  <Stack spacing={1.2}>
                    <LabelRow label="Invite Code" value={selectedFund.inviteCode} />
                    <LabelRow label="Invite Link" value={selectedFund.inviteLink} />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
                    <Button startIcon={<ContentCopyIcon />} variant="outlined" onClick={() => copyText(selectedFund.inviteCode, 'fund-code')} sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800, color: '#fff', borderColor: 'rgba(255,255,255,0.16)' }}>
                      {copied === 'fund-code' ? 'Invite Code Copied' : 'Copy Invite Code'}
                    </Button>
                    <Button startIcon={<ContentCopyIcon />} variant="outlined" onClick={() => copyText(selectedFund.inviteLink, 'fund-link')} sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800, color: '#fff', borderColor: 'rgba(255,255,255,0.16)' }}>
                      {copied === 'fund-link' ? 'Invite Link Copied' : 'Copy Invite Link'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20, mb: 1.8 }}>Contributors</Typography>
                  {(selectedFund.contributors || []).length === 0 ? (
                    <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>No contributions yet.</Typography>
                  ) : (
                    <Stack spacing={1.3}>
                      {selectedFund.contributors.map((item) => (
                        <Box key={item.id} sx={{ p: 2, borderRadius: 4, bgcolor: SOFT, border: BORDER }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography sx={{ color: '#fff', fontWeight: 900 }}>{item.memberName}</Typography>
                              <Typography sx={{ color: 'rgba(255,255,255,0.68)', mt: 0.4 }}>{new Date(item.createdAt).toLocaleString()}</Typography>
                            </Box>
                            <Typography sx={{ color: ORANGE, fontWeight: 900 }}>{money(item.amount, item.currency)}</Typography>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {view === 'community-receipt' && fundReceipt && (
            <>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 34 }}>Community Fund Receipt</Typography>
              <Card sx={{ borderRadius: 6, background: 'linear-gradient(135deg, rgba(34,18,82,0.98) 0%, rgba(91,46,255,0.94) 100%)', border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 28 }}>Contribution Recorded</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.74)', mt: 1.2, lineHeight: 1.6 }}>Your community-fund support has been logged with a digital proof trail.</Typography>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={1.25}>
                    <LabelRow label="Fund" value={fundReceipt.fund} />
                    <LabelRow label="Receipt Ref" value={fundReceipt.id} />
                    <LabelRow label="Status" value="SUCCESS" />
                    <LabelRow label="Date" value={fundReceipt.dateText} />
                    <LabelRow label="Member" value={fundReceipt.member} />
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={1.25}>
                    <LabelRow label="Amount" value={money(fundReceipt.amount, fundReceipt.currency)} />
                    <LabelRow label="Goal Amount" value={fundReceipt.goalAmount ? money(fundReceipt.goalAmount, fundReceipt.currency) : 'Open'} />
                    <LabelRow label="Invite Code" value={fundReceipt.inviteCode} />
                  </Stack>
                </CardContent>
              </Card>

              <Button variant="contained" onClick={() => setView('community-detail')} sx={{ borderRadius: 999, py: 1.4, textTransform: 'none', fontWeight: 900, bgcolor: ORANGE }}>
                Back to Fund
              </Button>
            </>
          )}
        </Stack>
      </Container>
    </Box>
  )
}
